import { NumericFormat } from 'react-number-format'

import { AIEmojisAPI, GetImageMetdataLink } from '@makyoapp/makyo-sdk'
import { FileCategory, ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  DateTimeColOptionsOnly,
  DEFAULT_DATETIME_COL_OPTIONS,
  fmtDt,
  formatNumberCol,
  ImageArrayValue,
} from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import {
  Chip,
  CloseCircle,
  ColorPickerV2,
  DateTimePicker,
  DropdownV2,
  Field,
  Label,
  MaterialIconPickerBody,
  Modal,
} from '@makyoapp/makyo-ui'

import { useLocaleColOrUserCb } from '@/hooks/useLocaleColOrUser'
import { ActionInputdata } from '@/shared/action.types'
import { Option } from '@/shared/types'
import { ActionInputV3 } from '@/components/ActionInputV3/ActionInputV3'
import { ColumnTypeIcon } from '@/components/ColumnTypeIcon'
import { ArrayEditor } from '@/components/GridV2/renderers-v2/ArrayEditor'
import { ImageManager } from '@/components/ImageManager/ImageManager'

interface ActionColumnFieldProps {
  tableId?: string
  label: string
  type: ColumnTypes
  value: ActionInputdata
  onChange(val: ActionInputdata): void
  error?: string | null
  colSettings?: Record<string, any>
  options?: Option[]
  isActionWorkflow?: boolean
}

export const ActionColumnField = ({
  tableId,
  label,
  type,
  value,
  onChange,
  error,
  colSettings,
  options,
  isActionWorkflow,
}: ActionColumnFieldProps) => {
  const localeCb = useLocaleColOrUserCb()

  return (
    <Field className='min-h-0'>
      <Label>{label}</Label>

      <div className='flex-1 px-0.5'>
        {(() => {
          if (
            [ColumnTypes.Text, ColumnTypes.Phone, ColumnTypes.Email, ColumnTypes.Address, ColumnTypes.URL].includes(
              type
            )
          ) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                value={value}
                onChange={onChange}
                icon={<ColumnTypeIcon type={type} />}
              />
            )
          }

          if (type === ColumnTypes.Number) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                inputType='number'
                formatDisplay={(val: any) => formatNumberCol(val as number, colSettings!)?.toString()}
                columnTypes={[type]}
                value={value}
                onChange={onChange}
                icon={<ColumnTypeIcon type={type} />}
              />
            )
          }

          if (type === ColumnTypes.RichText) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                inputType='textarea'
                value={value}
                onChange={onChange}
                icon={<ColumnTypeIcon type={type} />}
              />
            )
          }

          if (type === ColumnTypes.Image) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                fileTypes={[FileCategory.Image]}
                value={value}
                onChange={onChange}
                placeholder='Choose Image'
                renderCustomInput={({ value, onClear, onToggleCustomDropdown }: any) => {
                  return (
                    <div
                      role='button'
                      className='mk-bg-hover h-9 py-2 px-4 pr-8 rounded-sm flex items-center justify-between'
                      onClick={onToggleCustomDropdown}
                    >
                      <div className='flex items-center gap-2'>
                        <ColumnTypeIcon type={type} />
                        {!!value ? (
                          <div className='flex items-center gap-2 overflow-hidden'>
                            <img
                              src={GetImageMetdataLink(value, ImageMetadata.XS)}
                              className='w-[39px] h-[25px] object-cover'
                            />
                          </div>
                        ) : (
                          <div className='text-gray-400'>Choose Image</div>
                        )}
                      </div>

                      {value && (
                        <button type='button' className='flex items-center justify-center' onClick={onClear}>
                          <CloseCircle />
                        </button>
                      )}
                    </div>
                  )
                }}
                renderCustomDropdown={({ value, onChange, onToggleCustomDropdown }: any) => {
                  return (
                    <Modal
                      show
                      onHide={onToggleCustomDropdown}
                      backdropBlur
                      innerClassName='rounded-md overflow-hidden'
                    >
                      <Modal.Body noPadding className='md:w-[725px] min-h-[567px] border bdr'>
                        <ImageManager
                          modal={false}
                          id='mk-gg-img-editor'
                          show
                          onHide={() => {
                            onToggleCustomDropdown?.()
                          }}
                          initialSelected={value ? [value] : []}
                          onChange={(ty: any, v: any) => {
                            onChange?.({ value: v })
                          }}
                        />
                      </Modal.Body>
                    </Modal>
                  )
                }}
              />
            )
          }

          if (type === ColumnTypes.ImageArray) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                fileTypes={[FileCategory.Image]}
                value={value}
                onChange={onChange}
                placeholder='Choose Image'
                renderCustomInput={({ value, onClear, onToggleCustomDropdown }: any) => {
                  let imageArr = (value || []) as ImageArrayValue[]
                  return (
                    <div
                      role='button'
                      className='mk-bg-hover h-9 py-2 px-4 pr-8 rounded-sm flex items-center justify-between'
                      onClick={onToggleCustomDropdown}
                    >
                      <div className='flex items-center gap-2'>
                        <ColumnTypeIcon type={type} />
                        {!!imageArr.length ? (
                          <div className='flex items-center gap-2 overflow-hidden'>
                            {imageArr.map((img) => {
                              let imgPreview = GetImageMetdataLink(img.Url, ImageMetadata.XS)
                              return <img key={img.Url} src={imgPreview} className='w-[39px] h-[25px] object-cover' />
                            })}
                          </div>
                        ) : (
                          <div className='text-gray-400'>Choose Images</div>
                        )}
                      </div>

                      {!!imageArr.length && (
                        <button type='button' className='flex items-center justify-center' onClick={onClear}>
                          <CloseCircle />
                        </button>
                      )}
                    </div>
                  )
                }}
                renderCustomDropdown={({ value, onChange, onToggleCustomDropdown }: any) => {
                  return (
                    <Modal
                      show
                      onHide={onToggleCustomDropdown}
                      backdropBlur
                      innerClassName='rounded-md overflow-hidden'
                    >
                      <Modal.Body noPadding className='md:w-[725px] border bdr'>
                        <ImageManager
                          modal={false}
                          id='mk-gg-img-editor'
                          show
                          multiple
                          onHide={() => {
                            onToggleCustomDropdown?.()
                          }}
                          initialSelected={value || []}
                          onChange={(ty: any, v: any) => {
                            onChange?.({ value: v })
                          }}
                          innerClassname='relative min-h-[567px]'
                        />
                      </Modal.Body>
                    </Modal>
                  )
                }}
              />
            )
          }

          if (type === ColumnTypes.Attachment) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                value={value ?? ''}
                onChange={onChange}
                icon={<ColumnTypeIcon type={type} />}
              />
            )
          }

          if (type === ColumnTypes.Boolean) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                columnTypes={[ColumnTypes.Boolean]}
                placeholder='TRUE or FALSE'
                value={value}
                onChange={onChange}
                icon={<ColumnTypeIcon type={type} />}
                renderCustomInput={({ value }: any) => {
                  let boolValue =
                    typeof value === 'string' ? Boolean(value === 'TRUE') : typeof value === 'boolean' ? value : false

                  return (
                    <div
                      role='button'
                      className='mk-bg-hover min-h-9 max-h-20 overflow-y-auto py-2 px-4 pr-10 rounded-sm flex flex-wrap gap-1 items-center group'
                      onClick={() => {
                        onChange({ value: !boolValue })
                      }}
                    >
                      <div className='flex items-center gap-2'>
                        <ColumnTypeIcon type={type} />

                        <div className='ml-1 flex gap-2 items-center'>
                          <div className='uppercase group-hover:text-primary-600'>
                            {Boolean(boolValue) ? 'TRUE' : 'FALSE'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }}
              />
            )
          }

          if (type === ColumnTypes.DateTime) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                id='datetime'
                columnTypes={[ColumnTypes.DateTime]}
                value={value}
                onChange={onChange}
                renderCustomInput={({ value, onClear, onToggleCustomDropdown }: any) => {
                  const dtColSetting = { ...DEFAULT_DATETIME_COL_OPTIONS, ...colSettings }
                  const cellDateData = new Date(value as number)
                  const locale = localeCb(dtColSetting)
                  const displayDate = !isNaN(cellDateData.getTime()) ? fmtDt(cellDateData, locale, dtColSetting) : ''

                  return (
                    <div
                      role='button'
                      className='mk-bg-hover h-9 py-2 px-4 pr-8 rounded-sm flex items-center justify-between'
                      onClick={onToggleCustomDropdown}
                    >
                      <div className='flex items-center gap-2'>
                        <ColumnTypeIcon type={type} />

                        {value ? displayDate : <span className='text-gray-400'>Select Date</span>}
                      </div>

                      {value && (
                        <button type='button' className='flex items-center justify-center' onClick={onClear}>
                          <CloseCircle />
                        </button>
                      )}
                    </div>
                  )
                }}
                renderCustomDropdown={({ value, onChange, onToggleCustomDropdown }: any) => {
                  return (
                    <Modal show onHide={onToggleCustomDropdown} backdropBlur innerClassName='rounded-md'>
                      <Modal.Body noPadding>
                        <DateTimePicker
                          value={value}
                          onChange={(date) => {
                            onChange?.({ value: date })
                            onToggleCustomDropdown?.()
                          }}
                          onClose={() => {
                            onToggleCustomDropdown?.()
                          }}
                          showTime={(colSettings as DateTimeColOptionsOnly).dtDisplay !== 'date'}
                        />
                      </Modal.Body>
                    </Modal>
                  )
                }}
              />
            )
          }

          if (type === ColumnTypes.Icon) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                columnTypes={[ColumnTypes.Icon]}
                value={value}
                onChange={onChange}
                renderCustomInput={({ value, onClear, onToggleCustomDropdown }, triggerRef) => {
                  return (
                    <div
                      ref={triggerRef}
                      role='button'
                      className='mk-bg-hover h-9 py-2 px-4 pr-8 rounded-sm flex items-center justify-between'
                      onClick={onToggleCustomDropdown}
                    >
                      <div className='flex items-center gap-2'>
                        <ColumnTypeIcon type={type} />

                        {value ? (
                          <MaterialIcon size={20} icon={value} />
                        ) : (
                          <span className='text-gray-400'>Select Icon</span>
                        )}
                      </div>

                      {value && (
                        <button type='button' className='flex items-center justify-center' onClick={onClear}>
                          <CloseCircle />
                        </button>
                      )}
                    </div>
                  )
                }}
                renderCustomDropdown={({ value, onChange, onToggleCustomDropdown }, triggerRef) => {
                  return (
                    <DropdownV2 open onOpenChange={onToggleCustomDropdown}>
                      <DropdownV2.Trigger asChild>
                        {triggerRef && (
                          <div
                            className='fixed'
                            style={{
                              top: triggerRef.current?.getBoundingClientRect().top,
                              left: triggerRef.current?.getBoundingClientRect().left,
                              width: triggerRef.current?.getBoundingClientRect().width,
                              height: triggerRef.current?.getBoundingClientRect().height,
                            }}
                          />
                        )}
                      </DropdownV2.Trigger>

                      <DropdownV2.Content sideOffset={8} className='z-302 w-full text-m-m'>
                        <MaterialIconPickerBody
                          value={value?.value ?? ''}
                          onDone={(icon: any) => {
                            onChange?.({ value: icon })
                            onToggleCustomDropdown?.()
                          }}
                          settings={{ fill: 0, grade: 0, wght: 400, opsz: 24 }}
                          clickAsDone={true}
                          withClose={false}
                          searchMs={300}
                          searchFilter={async (keyword: string) => {
                            const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
                            return aiSearch
                          }}
                        />
                      </DropdownV2.Content>
                    </DropdownV2>
                  )
                }}
              />
            )
          }

          if (type === ColumnTypes.Array) {
            return (
              <>
                <ActionInputV3
                  options={options || []}
                  isActionWorkflow={isActionWorkflow}
                  columnTypes={[ColumnTypes.Array]}
                  value={value}
                  onChange={onChange}
                  renderCustomInput={({ value, onClear, onToggleCustomDropdown }, triggerRef) => {
                    let arrValue = (value as string[]) ?? []
                    return (
                      <div
                        role='button'
                        ref={triggerRef}
                        className='mk-bg-hover min-h-9 max-h-20 overflow-y-auto py-2 px-4 pr-10 rounded-sm flex flex-wrap gap-1 items-center'
                        onClick={onToggleCustomDropdown}
                      >
                        <div className='flex items-center gap-2'>
                          <ColumnTypeIcon type={type} />

                          {!!arrValue.length ? (
                            <>
                              {arrValue.map((c, i) => {
                                return (
                                  <Chip id={`item-${i}`} defaultSize={false} className='text-m-m' key={i}>
                                    {c}
                                  </Chip>
                                )
                              })}
                            </>
                          ) : (
                            <span className='text-gray-400'>Enter Value</span>
                          )}
                        </div>

                        {!!arrValue.length && (
                          <button
                            type='button'
                            className='absolute right-6 top-2.5 flex items-center justify-center'
                            onClick={onClear}
                          >
                            <CloseCircle />
                          </button>
                        )}
                      </div>
                    )
                  }}
                  renderCustomDropdown={({ value, onChange, onToggleCustomDropdown }, triggerRef) => {
                    return (
                      <DropdownV2 open onOpenChange={onToggleCustomDropdown}>
                        <DropdownV2.Trigger asChild>
                          {triggerRef && (
                            <div
                              className='fixed'
                              style={{
                                top: triggerRef.current?.getBoundingClientRect().top,
                                left: triggerRef.current?.getBoundingClientRect().left,
                                width: triggerRef.current?.getBoundingClientRect().width,
                                height: triggerRef.current?.getBoundingClientRect().height,
                              }}
                            />
                          )}
                        </DropdownV2.Trigger>

                        <DropdownV2.Content sideOffset={8} className='z-302 w-full text-m-m'>
                          <ArrayEditor
                            id='action-array-editor'
                            className='min-w-[400px]'
                            value={value?.value ?? []}
                            onChange={(nextValue: any) => {
                              if (nextValue && Array.isArray(nextValue)) {
                                onChange?.({ value: nextValue })
                              }

                              onToggleCustomDropdown?.()
                            }}
                          />
                        </DropdownV2.Content>
                      </DropdownV2>
                    )
                  }}
                />
              </>
            )
          }

          if (type === ColumnTypes.Rating) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                columnTypes={[ColumnTypes.Rating]}
                value={value}
                onChange={onChange}
                renderCustomInput={({ value, onClear }: any) => {
                  return (
                    <div>
                      <div className='mk-bg-hover h-9 py-2 px-4 pr-8 rounded-sm flex gap-2 items-center justify-between'>
                        <ColumnTypeIcon type={type} />

                        <NumericFormat
                          autoFocus={true}
                          className='mk-bg-hover h-9 py-2 pr-8 rounded-sm flex w-full'
                          decimalScale={2}
                          allowNegative={false}
                          thousandSeparator={','}
                          decimalSeparator={'.'}
                          max={5}
                          min={1}
                          value={Object.is(value, -0) ? '-' : (value ?? '')}
                          onValueChange={(val: any) => {
                            let currValue = parseFloat((val.floatValue ?? 0).toFixed(2))
                            let maxNum = parseFloat((5.0).toFixed(2))
                            let numValue = currValue > maxNum ? 5 : val.floatValue

                            onChange({
                              value: String(numValue),
                            })
                          }}
                        />
                      </div>

                      {!!value && (
                        <button
                          type='button'
                          className='absolute right-6 top-2.5 flex items-center justify-center'
                          onClick={onClear}
                        >
                          <CloseCircle />
                        </button>
                      )}
                    </div>
                  )
                }}
              />
            )
          }

          if (type === ColumnTypes.Color) {
            return (
              <ActionInputV3
                options={options || []}
                isActionWorkflow={isActionWorkflow}
                columnTypes={[ColumnTypes.Color]}
                value={value}
                onChange={onChange}
                renderCustomInput={({ value, onClear, onToggleCustomDropdown }, triggerRef) => {
                  let colorValue = value || ''

                  return (
                    <div
                      ref={triggerRef}
                      role='button'
                      className='mk-bg-hover min-h-9 max-h-20 overflow-y-auto py-2 px-4 pr-10 rounded-sm flex flex-wrap gap-1 items-center'
                      onClick={onToggleCustomDropdown}
                    >
                      <div className='flex items-center gap-2'>
                        <ColumnTypeIcon type={type} />

                        {colorValue ? (
                          <div className='flex gap-2 items-center'>
                            <span style={{ backgroundColor: colorValue }} className='w-4 h-4'></span>
                            <span className='uppercase'>{colorValue}</span>
                          </div>
                        ) : (
                          <span className='text-gray-400'>Enter Value</span>
                        )}
                      </div>

                      {colorValue && (
                        <button
                          type='button'
                          className='absolute right-6 top-2.5 flex items-center justify-center'
                          onClick={onClear}
                        >
                          <CloseCircle />
                        </button>
                      )}
                    </div>
                  )
                }}
                renderCustomDropdown={({ value, onChange, onToggleCustomDropdown }, triggerRef) => {
                  let colorValue = value || ''

                  return (
                    <DropdownV2 open onOpenChange={onToggleCustomDropdown}>
                      <DropdownV2.Trigger asChild>
                        {triggerRef && (
                          <div
                            className='fixed'
                            style={{
                              top: triggerRef.current?.getBoundingClientRect().top,
                              left: triggerRef.current?.getBoundingClientRect().left,
                              width: triggerRef.current?.getBoundingClientRect().width,
                              height: triggerRef.current?.getBoundingClientRect().height,
                            }}
                          />
                        )}
                      </DropdownV2.Trigger>

                      <DropdownV2.Content sideOffset={8} className='z-302 w-full text-m-m'>
                        <ColorPickerV2
                          onCancel={onToggleCustomDropdown}
                          noDropdown
                          modal
                          color={colorValue}
                          onChange={(color) => {
                            onChange?.({ value: color.hex })
                          }}
                        />
                      </DropdownV2.Content>
                    </DropdownV2>
                  )
                }}
              />
            )
          }

          return null
        })()}

        {error && <div className='mk-input-error'>{error}</div>}
      </div>
    </Field>
  )
}
